import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Alert,
  Stack, useMediaQuery, IconButton,
} from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { Bus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { TransportOptionCard } from "./TransportOptionCard";
import { TransportTabToggle } from "./TransportTabToggle";
import { CustomTransportBuilder } from "./CustomRoute/CustomTransportBuilder";
import { getBusOptionsFn } from "../../../../firebase/deliveryFunctions";
import { RouteTimeHeader } from "./RouteTimeHeader";

const TAB_KEYS = { AVAILABLE: "available", CUSTOM: "custom" };

// datetime-local string → unix millis
const toMillis = (datetimeLocalValue) => {
  if (!datetimeLocalValue) return null;
  return new Date(datetimeLocalValue).getTime();
};

// unix millis (or any ts) → datetime-local string
const toDatetimeLocal = (value) => {
  if (!value) return formatForDatetimeLocal(new Date());
  const date = typeof value === "number" ? new Date(value) : new Date(value);
  return formatForDatetimeLocal(date);
};

const formatForDatetimeLocal = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// onConfirm(price, busTransportData, startMs, endMs)
export const BusTransportModal = ({ open, onClose, origin, destination, pickupTime, onConfirm }) => {
  const theme = useMuiTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeTab, setActiveTab] = useState(TAB_KEYS.AVAILABLE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedTime, setSelectedTime] = useState(() => toDatetimeLocal(pickupTime));
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
  const modifiedOptionsRef = useRef({});
  const [customSteps, setCustomSteps] = useState(null);

  // Editable addresses — seeded from props, kept in sync if the parent changes them.
  const [originValue, setOriginValue] = useState(origin);
  const [destinationValue, setDestinationValue] = useState(destination);

  useEffect(() => { setOriginValue(origin); }, [origin]);
  useEffect(() => { setDestinationValue(destination); }, [destination]);

  useEffect(() => {
    if (pickupTime) setSelectedTime(toDatetimeLocal(pickupTime));
  }, [pickupTime]);

  useEffect(() => {
    if (data?.transportOptions?.length > 0) {
      setSelectedOptionIndex(0);
      modifiedOptionsRef.current = {};
    } else {
      setSelectedOptionIndex(null);
      modifiedOptionsRef.current = {};
    }
  }, [data]);

  const fetchBusTransportOptions = async (departureTime) => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      // Backend expects unix seconds
      const timestamp = departureTime ? Math.floor(toMillis(departureTime) / 1000) : null;
      const result = await getBusOptionsFn(originValue, destinationValue, timestamp);
      setData(result);
    } catch (err) {
      console.error("Bus transport fetch error:", err);
      setError(err.message || t("delivery.busTransportModal.generalError") || "Virhe haettaessa bussivaihtoehtoja");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !origin || !destination) return;
    fetchBusTransportOptions(selectedTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, origin, destination]);

  const handleOptionModified = useCallback((index, modifiedOption) => {
    modifiedOptionsRef.current[index] = modifiedOption;
  }, []);

  const handleCustomOptionBuilt = useCallback((steps) => {
    setCustomSteps(steps);
  }, []);

  const handleConfirm = () => {
    if (activeTab === TAB_KEYS.AVAILABLE) {
      if (selectedOptionIndex === null || !data) return;

      const selectedOption =
        modifiedOptionsRef.current[selectedOptionIndex] ||
        data.transportOptions[selectedOptionIndex];

      const startMs = selectedOption.start ?? toMillis(selectedTime);
      const endMs   = selectedOption.end   ?? null;

      onConfirm(selectedOption.price, selectedOption, startMs, endMs);
    } else {
      if (!customSteps || customSteps.length === 0) return;

      const customOption = {
        isCustom: true,
        legs: customSteps.map((step) => ({
          type: step.type,
          origin: step.departure,
          destination: step.arrival,
          start: step.departureTime || null,
          end: step.arrivalTime || null,
          ...(step.type === "bus-transport" && {
            busCompany: step.busCompany,
            busLine: step.busLine,
            shortName: [step.busCompany, step.busLine].filter(Boolean).join(" "),
          }),
          ...(step.type === "wait" && {
            inboundBusCompany: step.inboundBusCompany,
            inboundBusLine: step.inboundBusLine,
            outboundBusCompany: step.outboundBusCompany,
            outboundBusLine: step.outboundBusLine,
            shortName: "Bussinvaihto",
          }),
          ...(step.type === "car" && { shortName: "Autokuljetus" }),
        })),
        price: 1,
        name: "Oma reitti",
        start: customSteps[0]?.departureTime || null,
        end: customSteps[customSteps.length - 1]?.arrivalTime || null,
      };

      onConfirm(customOption.price, customOption, customOption.start, customOption.end);
    }
  };

  const isConfirmDisabled =
    activeTab === TAB_KEYS.AVAILABLE
      ? selectedOptionIndex === null || !data || loading
      : !customSteps || customSteps.length === 0;

  const tabs = [
    { key: TAB_KEYS.AVAILABLE, label: "Automaattiset vaihtoehdot" },
    { key: TAB_KEYS.CUSTOM,    label: "Luo oma" },
  ];

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, m: isMobile ? 0 : 2 } }}
    >
      <DialogTitle sx={{ pb: 1, pt: isMobile ? 2 : 2.5, px: isMobile ? 2 : 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Bus size={isMobile ? 22 : 24} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {t("delivery.busTransportModal.title") || "Bussipaketti"}
            </Typography>
          </Stack>
          {isMobile && (
            <IconButton onClick={onClose} size="small" sx={{ ml: "auto" }}>
              <X size={20} />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, px: isMobile ? 2 : 3, pb: isMobile ? 2 : 3 }}>
        {/* Route & Time — only relevant for the automatic search */}
        {activeTab === TAB_KEYS.AVAILABLE && (
          <RouteTimeHeader
            origin={originValue}
            destination={destinationValue}
            onOriginChange={setOriginValue}
            onDestinationChange={setDestinationValue}
            originInfo={data?.routeInfo?.origin ?? null}
            destinationInfo={data?.routeInfo?.destination ?? null}
            selectedTime={selectedTime}
            onTimeChange={setSelectedTime}
            onRefresh={() => fetchBusTransportOptions(selectedTime)}
            loading={loading}
            isMobile={isMobile}
          />
        )}

        <TransportTabToggle tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        {activeTab === TAB_KEYS.AVAILABLE && (
          <>
            {loading && <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {data && !loading && data.transportOptions && (
              <Box sx={{ position: "relative" }}>
                <Box sx={{ maxHeight: isMobile ? "50vh" : "60vh", overflowY: "auto", pr: 0.5 }}>
                  {data.transportOptions.map((option, index) => (
                    <TransportOptionCard
                      key={option.id || index}
                      option={option}
                      isSelected={selectedOptionIndex === index}
                      onSelect={() => setSelectedOptionIndex(index)}
                      onOptionModified={(modified) => handleOptionModified(index, modified)}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75, mb: 1, textAlign: "center" }}>
                  Vieritä nähdäksesi lisää vaihtoehtoja ↓
                  Jos et löydä sopivaa automaattista vaihtoehtoa, käytä <strong>"Luo oma"</strong> -välilehteä rakentaaksesi reitin.
                </Typography>
              </Box>
            )}
            {data && !loading && (!data.transportOptions || data.transportOptions.length === 0) && (
              <Alert severity="info">
                {t("delivery.busTransportModal.noOptions") || "Ei saatavilla olevia bussivaihtoehtoja tälle reitille."}
              </Alert>
            )}
          </>
        )}

        {activeTab === TAB_KEYS.CUSTOM && (
          <CustomTransportBuilder
            origin={originValue} destination={destinationValue} pickupTime={pickupTime}
            onOptionBuilt={handleCustomOptionBuilt}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: isMobile ? 2 : 3, pb: isMobile ? 2 : 2, pt: 1, gap: 1, flexDirection: isMobile ? "column" : "row" }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2, width: isMobile ? "100%" : "auto", order: isMobile ? 2 : 1 }}>
          {t("common.cancel") || "Peruuta"}
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={isConfirmDisabled} sx={{ borderRadius: 2, width: isMobile ? "100%" : "auto", order: isMobile ? 1 : 2 }}>
          Hyväksy
        </Button>
      </DialogActions>
    </Dialog>
  );
};